import type { InjectionKey } from "vue";

export type ValidationIssueParams = Record<string, string | number>;

export interface ValidationIssue {
  readonly messageKey: string;
  readonly params?: ValidationIssueParams;
}

export interface ValidationContext {
  readonly signal: AbortSignal;
  readonly fieldName: string;
  readonly displayName: string;
}

interface ValidationRuleBase {
  readonly name: string;
  readonly dependencies: readonly unknown[];
}

export interface SyncValidationRule<TValue> extends ValidationRuleBase {
  readonly kind: "sync";
  validate(value: TValue, context: ValidationContext): ValidationIssue | undefined;
}

export interface AsyncValidationRule<TValue> extends ValidationRuleBase {
  readonly kind: "async";
  readonly debounceMs?: number;
  validate(value: TValue, context: ValidationContext): Promise<ValidationIssue | undefined>;
}

export type ValidationRule<TValue> = SyncValidationRule<TValue> | AsyncValidationRule<TValue>;

export type ValidationStatus =
  | "pending-baseline"
  | "pending-validation"
  | "valid"
  | "invalid"
  | "disabled"
  | "unvalidated";

export interface AggregatedValidationEntry {
  readonly id: symbol;
  readonly name: string;
  readonly displayName: string;
  readonly path: readonly string[];
  readonly status: "invalid" | "pending-validation";
  readonly issue?: ValidationIssue;
}

export interface AggregatedDirtyField {
  readonly id: symbol;
  readonly name: string;
  readonly displayName: string;
  readonly path: readonly string[];
  readonly value: unknown;
  readonly baseline: unknown;
}

export interface FieldValidationController<TValue = unknown> {
  readonly id: symbol;
  readonly name: string;
  readonly displayName: string;
  readonly value: TValue;
  readonly baseline: TValue | undefined;
  readonly status: ValidationStatus;
  readonly issue: ValidationIssue | undefined;
  readonly valid: boolean | undefined;
  readonly invalid: boolean;
  readonly dirty: boolean;
  readonly touched: boolean;
  readonly pending: boolean;
  readonly baselinePending: boolean;
  readonly disabled: boolean;
  readonly hasRules: boolean;
  readonly participatesInValidation: boolean;
  readonly contributesDirty: boolean;
  readonly issues: readonly AggregatedValidationEntry[];
  validate(): Promise<boolean>;
  commitBaseline(): Promise<void>;
  resetToBaseline(): Promise<void>;
  beginBaselineSession(): Promise<void>;
  destroy(): void;
}

export interface FormValidationController {
  readonly id: symbol;
  readonly name: string;
  readonly displayName: string;
  readonly valid: boolean;
  readonly invalid: boolean;
  readonly canSubmit: boolean;
  readonly dirty: boolean;
  readonly touched: boolean;
  readonly pending: boolean;
  readonly baselinePending: boolean;
  readonly disabled: boolean;
  readonly hasRules: boolean;
  readonly participatesInValidation: boolean;
  readonly contributesDirty: boolean;
  readonly registeredFieldCount: number;
  readonly dirtyFields: readonly AggregatedDirtyField[];
  readonly issues: readonly AggregatedValidationEntry[];
  validate(): Promise<boolean>;
  validateAll(): Promise<boolean>;
  commitBaseline(): Promise<void>;
  resetToBaseline(): Promise<void>;
  commitAllBaselines(): Promise<void>;
  resetAllToBaselines(): Promise<void>;
  beginBaselineSession(): Promise<void>;
  destroy(): void;
}

export type ValidationNode = FieldValidationController | FormValidationController;

export interface FormValidationContext {
  readonly controller: FormValidationController;
  readonly baselinePending: boolean;
  readonly disabled: boolean;
  register(node: ValidationNode): () => void;
}

export const FormValidationContextKey: InjectionKey<FormValidationContext> =
  Symbol("FormValidationContext");

export type SubmitResult =
  | {
      readonly success: true;
      readonly afterCommit?: () => void | Promise<void>;
    }
  | {
      readonly success: false;
    };

export type SubmitHandler = () => Promise<SubmitResult>;

export type EqualityComparator<TValue> = (current: TValue, baseline: TValue) => boolean;
